import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Phone, Mail, MapPin, Send, CreditCard, Package, Scale, Gift, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { serviceAreas } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingForm = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    pickupDate: '',
    pickupTime: '',
    notes: ''
  });

  const [paymentOption, setPaymentOption] = useState('later'); // 'now' or 'later'
  const [selectedPackage, setSelectedPackage] = useState('');
  const [packages, setPackages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [estimatedWeight, setEstimatedWeight] = useState('');
  const [paymentUrl, setPaymentUrl] = useState(null); // For Safari mobile - show payment link
  const [bookingCreated, setBookingCreated] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false); // For Pay Later success state
  const [successBookingDetails, setSuccessBookingDetails] = useState(null); // Store booking details for success display
  const [agreedToTerms, setAgreedToTerms] = useState(false); // Terms & Conditions checkbox;

  // Check for logged-in customer and fetch packages
  useEffect(() => {
    const storedCustomer = sessionStorage.getItem('customer');
    if (storedCustomer) {
      const customerData = JSON.parse(storedCustomer);
      setCustomer(customerData);
      // Pre-fill form with customer data
      setFormData(prev => ({
        ...prev,
        name: customerData.name || '',
        phone: customerData.phone || '',
        email: customerData.email || ''
      }));
    }

    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/payments/packages`);
        if (response.data.success) {
          setPackages(response.data.packages);
          // Set default to medium package
          if (response.data.packages.length > 0) {
            setSelectedPackage('medium');
          }
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        toast.error('Failed to load payment packages');
      } finally {
        setLoadingPackages(false);
      }
    };

    fetchPackages();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.city || !formData.pickupDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (paymentOption === 'now' && !selectedPackage) {
      toast.error('Please select a package for payment');
      return;
    }

    // Validate weight for per-pound pricing
    const selectedPkg = getSelectedPackageDetails();
    if (paymentOption === 'now' && selectedPkg?.is_per_pound) {
      const weight = parseFloat(estimatedWeight);
      if (!weight || weight < (selectedPkg.min_weight || 15)) {
        toast.error(`Please enter weight (minimum ${selectedPkg.min_weight || 15} lbs)`);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Build booking payload
      const bookingPayload = {
        ...formData,
        customer_id: customer?.id || null,
        is_guest: !customer,
        package_id: paymentOption === 'now' ? selectedPackage : null,
        estimated_weight: selectedPkg?.is_per_pound ? parseFloat(estimatedWeight) : null
      };

      // Step 1: Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, bookingPayload);
      
      if (bookingResponse.data.success) {
        const bookingId = bookingResponse.data.booking.id;
        const bookingMessage = bookingResponse.data.message;
        
        // Step 2: Handle payment based on option
        if (paymentOption === 'now') {
          // Create Stripe checkout session
          const paymentPayload = {
            package_id: selectedPackage,
            booking_id: bookingId,
            origin_url: window.location.origin
          };
          
          // Add weight for per-pound pricing
          if (selectedPkg?.is_per_pound && estimatedWeight) {
            paymentPayload.weight_lbs = parseFloat(estimatedWeight);
          }

          console.log('Creating payment session...', paymentPayload);
          const paymentResponse = await axios.post(`${API}/payments/checkout/session`, paymentPayload);
          console.log('Payment response:', paymentResponse.data);

          if (paymentResponse.data.success && paymentResponse.data.url) {
            const stripeUrl = paymentResponse.data.url;
            console.log('Stripe URL:', stripeUrl);
            
            // Store payment URL and show payment button (Safari mobile friendly)
            setPaymentUrl(stripeUrl);
            setBookingCreated(true);
            setIsSubmitting(false);
            toast.success('Booking created! Click the button below to complete payment.');
          } else {
            console.error('Payment session failed:', paymentResponse.data);
            toast.error('Failed to initiate payment. Please try again.');
            setIsSubmitting(false);
          }
        } else {
          // Pay later - show success message (may include loyalty message)
          toast.success(bookingMessage);
          
          // Store booking details for success display
          setSuccessBookingDetails({
            name: formData.name,
            pickupDate: formData.pickupDate,
            pickupTime: formData.pickupTime,
            city: formData.city,
            message: bookingMessage
          });
          setBookingSuccess(true);
          
          // Reset form (keep customer info if logged in)
          if (customer) {
            setFormData(prev => ({
              name: customer.name,
              phone: customer.phone,
              email: customer.email,
              address: '',
              city: '',
              pickupDate: '',
              pickupTime: '',
              notes: ''
            }));
          } else {
            setFormData({
              name: '',
              phone: '',
              email: '',
              address: '',
              city: '',
              pickupDate: '',
              pickupTime: '',
              notes: ''
            });
          }
          setPaymentOption('later');
          setEstimatedWeight('');
        }
      } else {
        toast.error('Failed to submit booking. Please try again.');
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Something went wrong. Please try again or call us directly.');
      }
    } finally {
      if (paymentOption === 'later') {
        setIsSubmitting(false);
      }
      // If paying now, don't reset isSubmitting as we're redirecting
    }
  };

  const getSelectedPackageDetails = () => {
    return packages.find(pkg => pkg.id === selectedPackage);
  };

  const calculatePerPoundPrice = () => {
    const pkg = getSelectedPackageDetails();
    if (pkg?.is_per_pound && estimatedWeight) {
      return (parseFloat(estimatedWeight) * pkg.price_per_lb).toFixed(2);
    }
    return null;
  };

  // Separate packages into flat-rate and per-pound
  const flatRatePackages = packages.filter(pkg => !pkg.is_per_pound);
  const perPoundPackage = packages.find(pkg => pkg.is_per_pound);

  return (
    <section id="book-now" className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="shadow-2xl border-2 border-teal-100">
          <CardHeader className="text-center bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-lg">
            <CardTitle className="text-3xl sm:text-4xl font-bold mb-2">
              Book Your Free Pickup
            </CardTitle>
            <CardDescription className="text-teal-50 text-lg">
              Schedule a convenient time and choose your payment option
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6 sm:p-8">
            {/* Payment Ready - Show after booking created */}
            {bookingCreated && paymentUrl && (
              <div className="mb-6 p-6 bg-green-50 rounded-lg border-2 border-green-300 text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <CreditCard className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-green-800">Booking Created!</h3>
                  <p className="text-green-700 mt-2">Complete your payment using the link below:</p>
                </div>
                
                {/* Payment link that opens in new tab */}
                <a
                  href={paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full sm:w-auto px-8 py-4 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold rounded-lg transition-colors no-underline mb-3"
                >
                  💳 Open Payment Page
                </a>
                
                <p className="text-xs text-gray-500 mb-4">
                  Opens in new tab. If blocked, copy the link below.
                </p>
                
                {/* Copy link section */}
                <div className="p-4 bg-white rounded-lg border-2 border-blue-200">
                  <p className="text-sm font-semibold text-blue-800 mb-2">Or copy payment link:</p>
                  
                  {/* Selectable text area */}
                  <textarea
                    id="paymentUrlTextarea"
                    readOnly
                    value={paymentUrl}
                    onClick={(e) => e.target.select()}
                    className="w-full p-3 text-xs border-2 border-blue-300 rounded bg-blue-50 text-blue-800 resize-none"
                    rows={2}
                    style={{ fontSize: '11px', wordBreak: 'break-all' }}
                  />
                  
                  <div className="mt-2 flex gap-2 justify-center flex-wrap">
                    <button
                      onClick={() => {
                        const textarea = document.getElementById('paymentUrlTextarea');
                        if (textarea) {
                          textarea.select();
                          textarea.setSelectionRange(0, 99999);
                          try {
                            const successful = document.execCommand('copy');
                            if (successful) {
                              toast.success('Copied! Paste in browser address bar');
                            } else {
                              toast.info('Selected! Press Cmd+C or Ctrl+C to copy');
                            }
                          } catch (err) {
                            toast.info('Selected! Press Cmd+C or Ctrl+C to copy');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => {
                        const textarea = document.getElementById('paymentUrlTextarea');
                        if (textarea) {
                          textarea.select();
                          textarea.setSelectionRange(0, 99999);
                        }
                      }}
                      className="px-4 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                    >
                      Select All
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    1. Tap &quot;Copy Link&quot; or &quot;Select All&quot; then copy<br/>
                    2. Open Safari/Chrome<br/>
                    3. Paste link in address bar
                  </p>
                </div>
                
                <button 
                  onClick={() => {
                    setBookingCreated(false);
                    setPaymentUrl(null);
                  }}
                  className="mt-4 text-sm text-gray-500 underline hover:text-gray-700"
                >
                  Cancel and book again
                </button>
              </div>
            )}

            {/* Success State for Pay Later bookings */}
            {bookingSuccess && !bookingCreated && (
              <div className="p-8 bg-gradient-to-br from-green-50 to-teal-50 rounded-lg border-2 border-green-300 text-center">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-green-800 mb-2">Booking Confirmed!</h3>
                <p className="text-green-700 mb-4">{successBookingDetails?.message || 'We will contact you shortly.'}</p>
                
                <div className="bg-white rounded-lg p-4 mb-6 inline-block text-left">
                  <h4 className="font-semibold text-gray-800 mb-2">Pickup Details:</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Name:</strong> {successBookingDetails?.name}</p>
                    <p><strong>Date:</strong> {successBookingDetails?.pickupDate}</p>
                    <p><strong>Time:</strong> {successBookingDetails?.pickupTime || 'We will confirm'}</p>
                    <p><strong>City:</strong> {successBookingDetails?.city}</p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 text-sm">
                    <strong>💰 Payment:</strong> You will pay when we collect or deliver your laundry.
                  </p>
                </div>
                
                <p className="text-gray-600 mb-4">We'll call you to confirm the pickup time.</p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => {
                      setBookingSuccess(false);
                      setSuccessBookingDetails(null);
                    }}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Book Another Pickup
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="border-teal-600 text-teal-600 hover:bg-teal-50"
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            )}

            {/* Show form only if payment not ready */}
            {!bookingCreated && !bookingSuccess && (
              <>
                {/* Loyalty Banner for logged-in customers */}
                {customer && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-teal-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Gift className="text-purple-600" size={24} />
                        <div>
                          <p className="font-semibold text-gray-900">Welcome back, {customer.name}!</p>
                          <p className="text-sm text-gray-600">
                            {customer.loyalty_count >= 10 
                              ? '🎉 Your next service is FREE!' 
                              : `${10 - (customer.loyalty_count || 0)} more washes until your FREE service!`
                            }
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">{customer.loyalty_count || 0}/10</div>
                        <p className="text-xs text-gray-500">Loyalty Points</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Login prompt for guests */}
                {!customer && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <LogIn className="text-blue-600" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">Have an account?</p>
                      <p className="text-sm text-gray-600">Log in to track rewards - 11th wash is FREE!</p>
                    </div>
                  </div>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => navigate('/login')}
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    Login
                  </Button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User size={16} className="text-teal-600" />
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                    disabled={!!customer}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone size={16} className="text-teal-600" />
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                    disabled={!!customer}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-teal-600" />
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <MapPin size={16} className="text-teal-600" />
                  Pickup Address
                </label>
                <Input
                  type="text"
                  placeholder="123 Main Street, Apt 4B"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>

              {/* Pickup Details */}
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin size={16} className="text-teal-600" />
                    City *
                  </label>
                  <Select value={formData.city} onValueChange={(value) => handleChange('city', value)}>
                    <SelectTrigger className="border-gray-300 focus:border-teal-500 focus:ring-teal-500">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceAreas.map((area) => (
                        <SelectItem key={area.id} value={area.city}>
                          {area.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar size={16} className="text-teal-600" />
                    Pickup Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.pickupDate}
                    onChange={(e) => handleChange('pickupDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock size={16} className="text-teal-600" />
                    Pickup Time
                  </label>
                  <Select value={formData.pickupTime} onValueChange={(value) => handleChange('pickupTime', value)}>
                    <SelectTrigger className="border-gray-300 focus:border-teal-500 focus:ring-teal-500">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9:00-10:00">9:00 AM - 10:00 AM</SelectItem>
                      <SelectItem value="10:00-11:00">10:00 AM - 11:00 AM</SelectItem>
                      <SelectItem value="11:00-12:00">11:00 AM - 12:00 PM</SelectItem>
                      <SelectItem value="12:00-13:00">12:00 PM - 1:00 PM</SelectItem>
                      <SelectItem value="13:00-14:00">1:00 PM - 2:00 PM</SelectItem>
                      <SelectItem value="14:00-15:00">2:00 PM - 3:00 PM</SelectItem>
                      <SelectItem value="15:00-16:00">3:00 PM - 4:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Payment Option */}
              <div className="space-y-4 border-t pt-6">
                <label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard size={20} className="text-teal-600" />
                  Payment Option
                </label>
                
                <div className="space-y-3">
                  <div 
                    onClick={() => setPaymentOption('now')}
                    className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentOption === 'now' 
                        ? 'border-teal-600 bg-teal-50' 
                        : 'border-gray-300 hover:border-teal-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentOption === 'now' ? 'border-teal-600' : 'border-gray-400'
                    }`}>
                      {paymentOption === 'now' && (
                        <div className="w-3 h-3 rounded-full bg-teal-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Pay Now (Online Payment)</div>
                      <div className="text-sm text-gray-500">Secure payment via Stripe - Get instant confirmation</div>
                    </div>
                  </div>
                  
                  <div 
                    onClick={() => setPaymentOption('later')}
                    className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentOption === 'later' 
                        ? 'border-teal-600 bg-teal-50' 
                        : 'border-gray-300 hover:border-teal-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentOption === 'later' ? 'border-teal-600' : 'border-gray-400'
                    }`}>
                      {paymentOption === 'later' && (
                        <div className="w-3 h-3 rounded-full bg-teal-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Pay Later (At Pickup/Delivery)</div>
                      <div className="text-sm text-gray-500">Pay when we collect or deliver your laundry</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Package Selection (only if Pay Now) */}
              {paymentOption === 'now' && (
                <div className="space-y-4 bg-teal-50 p-6 rounded-lg border border-teal-200">
                  <label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Package size={20} className="text-teal-600" />
                    Select Pricing Option
                  </label>
                  
                  {loadingPackages ? (
                    <div className="text-center py-4">Loading packages...</div>
                  ) : (
                    <div className="space-y-6">
                      {/* Per-Pound Option */}
                      {perPoundPackage && (
                        <div className="space-y-3">
                          <h4 className="font-medium text-gray-700 flex items-center gap-2">
                            <Scale size={16} className="text-teal-600" />
                            Pay Per Pound
                          </h4>
                          <div
                            onClick={() => setSelectedPackage(perPoundPackage.id)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedPackage === perPoundPackage.id
                                ? 'border-teal-600 bg-white shadow-lg'
                                : 'border-gray-300 bg-white hover:border-teal-400'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-bold text-lg text-gray-900">{perPoundPackage.name}</div>
                              <div className="text-2xl font-bold text-teal-600">${perPoundPackage.price_per_lb}/lb</div>
                            </div>
                            <div className="text-sm text-gray-600">{perPoundPackage.description}</div>
                            <div className="text-xs text-gray-500 mt-1">${perPoundPackage.min_weight} minimum order</div>
                          </div>

                          {/* Weight input for per-pound */}
                          {selectedPackage === perPoundPackage.id && (
                            <div className="mt-3 p-4 bg-white rounded-lg border border-teal-200">
                              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                                <Scale size={14} className="text-teal-600" />
                                Estimated Weight (lbs) *
                              </label>
                              <Input
                                type="number"
                                placeholder="Enter estimated weight"
                                value={estimatedWeight}
                                onChange={(e) => setEstimatedWeight(e.target.value)}
                                min={perPoundPackage.min_weight}
                                step="0.5"
                                className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                              />
                              {estimatedWeight && parseFloat(estimatedWeight) >= perPoundPackage.min_weight && (
                                <div className="mt-2 text-right">
                                  <span className="text-sm text-gray-600">Estimated Total: </span>
                                  <span className="text-xl font-bold text-teal-600">${calculatePerPoundPrice()}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Flat Rate Options */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-700 flex items-center gap-2">
                          <Package size={16} className="text-teal-600" />
                          Flat Rate Packages
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-4">
                          {flatRatePackages.map((pkg) => (
                            <div
                              key={pkg.id}
                              onClick={() => {
                                setSelectedPackage(pkg.id);
                                setEstimatedWeight(''); // Clear weight when selecting flat rate
                              }}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                selectedPackage === pkg.id
                                  ? 'border-teal-600 bg-white shadow-lg'
                                  : 'border-gray-300 bg-white hover:border-teal-400'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="font-bold text-lg text-gray-900">{pkg.name}</div>
                                <div className="text-2xl font-bold text-teal-600">${pkg.price}</div>
                              </div>
                              <div className="text-sm text-gray-600 mb-2">{pkg.description}</div>
                              {pkg.weight_lbs && (
                                <div className="text-xs text-gray-500">Up to {pkg.weight_lbs} lbs</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Selected Package Summary */}
                      {selectedPackage && getSelectedPackageDetails() && (
                        <div className="mt-4 p-4 bg-white rounded-lg border border-teal-300">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-semibold text-gray-900">Selected: {getSelectedPackageDetails().name}</div>
                              <div className="text-sm text-gray-600">{getSelectedPackageDetails().description}</div>
                            </div>
                            <div className="text-3xl font-bold text-teal-600">
                              {getSelectedPackageDetails().is_per_pound 
                                ? (estimatedWeight ? `$${calculatePerPoundPrice()}` : `$${getSelectedPackageDetails().price_per_lb}/lb`)
                                : `$${getSelectedPackageDetails().price}`
                              }
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Additional Notes
                </label>
                <Textarea
                  placeholder="Any special instructions or preferences..."
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                  className="border-gray-300 focus:border-teal-500 focus:ring-teal-500 resize-none"
                />
              </div>

              {/* Info box */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <p className="text-sm text-teal-800">
                  <strong>Note:</strong> Same-day service available for qualifying orders picked up before 4:00 PM. 
                  Pickups after 4:00 PM will be delivered the following day.
                </p>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    required
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    By scheduling this pickup, I confirm that I have read, understood, and agree to the{' '}
                    <a 
                      href="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-semibold text-teal-600 underline hover:text-teal-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Wash & Fold Terms & Conditions
                    </a>. 
                    I acknowledge that <span className="font-semibold">Family Laundry LLC</span> is not responsible 
                    for loss, damage, shrinkage, or normal wear and tear of items beyond the cost of the service provided.
                  </span>
                </label>
                {!agreedToTerms && (
                  <p className="text-xs text-amber-600 mt-2 ml-8">
                    ⚠️ You must agree to the Terms & Conditions to proceed.
                  </p>
                )}
              </div>

              {/* Submit button */}
              <Button
                type="submit"
                disabled={isSubmitting || (paymentOption === 'now' && loadingPackages) || !agreedToTerms}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    {paymentOption === 'now' ? (
                      <>
                        <CreditCard className="mr-2" size={20} />
                        Proceed to Payment
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Schedule Pickup
                      </>
                    )}
                  </>
                )}
              </Button>
            </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BookingForm;
